'use client';

import { startTransition, useEffect, useState } from 'react';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/InstallmentCalculatorDialog/InstallmentCalculatorDialog.client';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { SideBySideWithTippBadge } from '../SideBySideWithTippBadge/SideBySideWithTippBadge.client';
import { SideBySideWithIconBadge } from '../SideBySideWithIconBadge/SideBySideWithIconBadge.client';
import { IconInstallmentsAndLegalHints } from '../IconInstallmentsAndLegalHints/IconInstallmentsAndLegalHints.client';

export type InstallmentCalulatorInDialogProps = {
  /**
   * the single installment value from the calculation result as integer (e.g. 1337)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * Product price value as int (e.g. 1337)
   */
  basePrice: number;
};

export const InstallmentCalulatorInDialog = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  basePrice,
}: InstallmentCalulatorInDialogProps) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const {
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
  } = useConfig();

  const { trackGlycerinDisplayLink, trackDialogOpen } = useInstallmentCalculatorTracking();

  useEffect(() => {
    trackGlycerinDisplayLink();
  }, [trackGlycerinDisplayLink]);

  useEffect(() => {
    if (dialogOpened) {
      trackDialogOpen();
    }
  }, [dialogOpened, trackDialogOpen]);

  return (
    <>
      <InstallmentCalculatorDialog
        basePrice={basePrice}
        isOpen={dialogOpened}
        onClose={() => startTransition(() => setDialogOpened(false))}
      />
      {(() => {
        switch (InstallmentCalculatorCallToActionLayout?.layout) {
          case 'SideBySideWithTippBadge': {
            return (
              <SideBySideWithTippBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalulatorHandler={() => startTransition(() => setDialogOpened(true))}
              />
            );
          }
          case 'SideBySideWithIconBadge': {
            return (
              <SideBySideWithIconBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalulatorHandler={() => startTransition(() => setDialogOpened(true))}
              />
            );
          }
          case 'IconInstallmentsAndLegalHints': {
            return (
              <IconInstallmentsAndLegalHints
                singleInstallment={singleInstallment}
                currency={currency}
                openInstallmentCalulatorHandler={() => startTransition(() => setDialogOpened(true))}
              />
            );
          }
          default:
            return null;
        }
      })()}
    </>
  );
};
